import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';

@Component({
  selector: 'app-team-member-card',
  standalone: false,
  templateUrl: './team-member-card.component.html',
  styleUrls: ['./team-member-card.component.scss']
})
export class TeamMemberCardComponent {
  @ViewChild('photoInput') photoInput!: ElementRef<HTMLInputElement>;

  /** The reactive FormGroup backing this card's fields. */
  @Input() memberForm!: FormGroup;
  /** URL of the already-uploaded photo to display inside the circle. */
  @Input() photoUrl = '';
  /** Optional heading shown at the top of the card. */
  @Input() cardTitle = '';
  /** Control name for the combined Full Name & Title field. */
  @Input() nameTitleControlName = 'nameTitle';
  /** Control name for the Email field. */
  @Input() emailControlName = 'email';
  /** Control name for the Bio field. */
  @Input() bioControlName = 'bio';
  /** When non-empty, a LinkedIn field is rendered using this control name. */
  @Input() linkedinControlName = '';
  /** Show the yellow commit / add button. */
  @Input() showCommitButton = false;
  /** Label for the commit button (falls back to translation key). */
  @Input() commitLabel = '';
  /** Show the remove button. */
  @Input() showRemoveButton = false;
  /** Label for the remove button (falls back to translation key). */
  @Input() removeLabel = '';
  /** Optional API-driven required overrides (takes precedence over Validators.required). */
  @Input() nameTitleRequired?: boolean;
  @Input() emailRequired?: boolean;
  @Input() bioRequired?: boolean;
  @Input() linkedinRequired?: boolean;
  /** Optional API-driven visibility overrides; false hides the field entirely. */
  @Input() nameTitleVisible?: boolean;
  @Input() emailVisible?: boolean;
  @Input() bioVisible?: boolean;
  @Input() linkedinVisible?: boolean;

  /** Emits the selected File when the user picks a valid image. */
  @Output() photoFileSelected = new EventEmitter<File>();
  /** Emits when the commit / add button is clicked. */
  @Output() commit = new EventEmitter<void>();
  /** Emits when the remove button is clicked. */
  @Output() remove = new EventEmitter<void>();

  private readonly allowedImageTypes = [
    'image/jpeg', 'image/jpg', 'image/bmp', 'image/png', 'image/jfif', 'image/webp'
  ];

  onPhotoClick(): void {
    this.photoInput.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.allowedImageTypes.includes(file.type)) return;
    this.photoFileSelected.emit(file);
  }

  isRequired(controlName: string): boolean {
    return this.memberForm.get(controlName)?.hasValidator(Validators.required) ?? false;
  }

  onCommit(): void {
    this.commit.emit();
  }

  onRemove(): void {
    this.remove.emit();
  }
}
